import { useMemo, useState } from "react";
import { api, type MailFolder, type PublicMailAccount } from "../../lib/api";
import { useConfirm } from "../shell/ConfirmDialog";

const SYSTEM_RE = /^(inbox|trash|junk|spam|sent|drafts|archive)$/i;

function isProtectedFolder(path: string) {
  if (path.toUpperCase() === "INBOX") return true;
  const base = path.split(/[./]/).pop() || path;
  return SYSTEM_RE.test(base);
}

export function FolderManagerModal({
  account,
  folders,
  onClose,
  onChanged,
}: {
  account: PublicMailAccount;
  folders: MailFolder[];
  onClose: () => void;
  onChanged: (folders: MailFolder[]) => void;
}) {
  const { confirm } = useConfirm();
  const [name, setName] = useState("");
  const [parent, setParent] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [renamePath, setRenamePath] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const delimiter = useMemo(() => folders.find((f) => f.delimiter)?.delimiter || "/", [folders]);

  const sorted = useMemo(
    () => [...folders].sort((a, b) => a.path.localeCompare(b.path, undefined, { sensitivity: "base" })),
    [folders],
  );

  async function create() {
    const leaf = name.trim();
    if (!leaf) {
      setError("Inserisci un nome cartella");
      return;
    }
    const path = parent ? `${parent}${delimiter}${leaf}` : leaf;
    setBusy(true);
    setError("");
    try {
      const res = await api.createFolder(account.id, path);
      onChanged(res.folders);
      setName("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(path: string) {
    if (isProtectedFolder(path)) {
      setError("Cartella di sistema non eliminabile");
      return;
    }
    if (
      !(await confirm({
        title: "Elimina cartella",
        message: `Eliminare la cartella «${path}»? I messaggi al suo interno potrebbero andare persi sul server.`,
        confirmLabel: "Elimina",
        tone: "danger",
      }))
    ) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await api.deleteFolder(account.id, path);
      onChanged(res.folders);
      if (renamePath === path) setRenamePath(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function applyRename() {
    if (!renamePath) return;
    const next = renameValue.trim();
    if (!next || next === renamePath) {
      setRenamePath(null);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await api.renameFolder(account.id, renamePath, next);
      onChanged(res.folders);
      setRenamePath(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal modal-folder-mgr" onClick={(e) => e.stopPropagation()}>
        <div className="composer-chrome">
          <div>
            <div className="eyebrow">Cartelle</div>
            <strong className="composer-title">Gestisci cartelle · {account.email}</strong>
          </div>
          <button className="btn btn-ghost" type="button" onClick={onClose} aria-label="Chiudi">
            ✕
          </button>
        </div>

        <div className="folder-mgr-body">
          <section className="folder-mgr-create">
            <h3>Nuova cartella</h3>
            <div className="folder-mgr-create-row">
              <label className="filter-field">
                <span>Dentro</span>
                <select className="select" value={parent} onChange={(e) => setParent(e.target.value)}>
                  <option value="">Radice mailbox</option>
                  {sorted.map((f) => (
                    <option key={f.path} value={f.path}>
                      {f.path}
                    </option>
                  ))}
                </select>
              </label>
              <label className="filter-field" style={{ flex: 1 }}>
                <span>Nome</span>
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="es. Progetti"
                  onKeyDown={(e) => e.key === "Enter" && void create()}
                />
              </label>
              <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void create()}>
                Crea
              </button>
            </div>
          </section>

          <section className="folder-mgr-list">
            <h3>Esistenti ({sorted.length})</h3>
            <div className="folder-mgr-rows">
              {sorted.map((f) => {
                const protectedFolder = isProtectedFolder(f.path);
                const renaming = renamePath === f.path;
                return (
                  <div key={f.path} className="folder-mgr-row">
                    {renaming ? (
                      <input
                        className="input"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void applyRename();
                          if (e.key === "Escape") setRenamePath(null);
                        }}
                        autoFocus
                      />
                    ) : (
                      <div className="folder-mgr-meta">
                        <strong>{f.name}</strong>
                        <em>{f.path}</em>
                        {f.specialUse && <span className="folder-mgr-tag">{f.specialUse}</span>}
                      </div>
                    )}
                    <div className="actions">
                      {renaming ? (
                        <>
                          <button className="btn btn-primary btn-sm" type="button" disabled={busy} onClick={() => void applyRename()}>
                            Salva
                          </button>
                          <button className="btn btn-sm" type="button" onClick={() => setRenamePath(null)}>
                            Annulla
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn btn-sm"
                            type="button"
                            disabled={busy || protectedFolder}
                            title={protectedFolder ? "Cartella di sistema" : "Rinomina"}
                            onClick={() => {
                              setRenamePath(f.path);
                              setRenameValue(f.path);
                            }}
                          >
                            Rinomina
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            type="button"
                            disabled={busy || protectedFolder}
                            title={protectedFolder ? "Cartella di sistema" : "Elimina"}
                            onClick={() => void remove(f.path)}
                          >
                            Elimina
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
          {error && <div className="error">{error}</div>}
        </div>
      </div>
    </div>
  );
}
